import React, { useEffect } from 'react';
import * as styles from './AutomationPromoBanner.module.scss';
import {
  IconChecklist,
  IconChecklistItem,
} from 'components/shared/IconChecklist';
import { Button, Heading, Message, Text } from 'components/core';
import { CHOOSE_YOUR_TEMPLATE_ROUTE, tokenizeRoute } from 'query/routes';
import { getReviewId } from 'query/review';
import { GrowItem, Inline, Stack } from 'components/layout';
import automationPreview from 'assets/images/automation-promo.png';
import usePersistedState from 'hooks/usePersistedState';
import { sendEvent } from 'util/pendoTrack';

export const BANNER_DISMISSED_KEY = 'automationBannerSeen';
export const AUTOMATION_PROMO_BANNER_SHOWN_EVENT =
  'covidence.review.automation_promo_banner_shown';

export const AutomationPromoBanner = () => {
  const [userDismissedMessage, setUserDismissedMessage] = usePersistedState(
    BANNER_DISMISSED_KEY,
    false
  );
  const reviewId = getReviewId();
  const route = tokenizeRoute(CHOOSE_YOUR_TEMPLATE_ROUTE, {
    review_id: reviewId,
    backTo: 'study_list',
    type: 'de',
  });
  const dismissBanner = () => setUserDismissedMessage(true);

  useEffect(() => {
    if (!userDismissedMessage) {
      sendEvent(AUTOMATION_PROMO_BANNER_SHOWN_EVENT, {
        review_id: reviewId,
        path: window.location.pathname,
      });
    }
  }, []);

  if (userDismissedMessage) {
    return null;
  }

  return (
    <Message
      className={styles.message}
      icon={false}
      type="primary"
      variant="prominent"
      onClose={dismissBanner}
      size="lg"
    >
      <Inline
        className={styles.bannerContent}
        spacing={1}
        shouldWrap={false}
        alignY="top"
      >
        <GrowItem>
          <Stack spacing={3}>
            <Heading type="h1" component="h3" variant="neutral">
              Capture data faster with automation
            </Heading>
            <Text>
              Start with a template shaped around your review question. Then
              work alongside each study&apos;s full text, verify suggested
              values against the source, and export clean, structured data for
              synthesis.
            </Text>

            <IconChecklist>
              <IconChecklistItem>
                <Text>
                  <strong>Capture what matters, faster:</strong> Tailor your
                  template to the data your review needs, then use automated
                  suggestions to reduce manual entry.
                </Text>
              </IconChecklistItem>
              <IconChecklistItem>
                <Text>
                  <strong>Everything in one place:</strong> Extract directly
                  alongside each study&apos;s full text, without jumping between
                  tabs or tools.
                </Text>
              </IconChecklistItem>

              <IconChecklistItem>
                <Text>
                  <strong>Export for analysis:</strong> Produce clean,
                  structured data in Excel for synthesis.
                </Text>
              </IconChecklistItem>
            </IconChecklist>
            <div>
              <Button
                type="brand"
                to={route}
                data-pendo-key="automation-promo-cta"
              >
                Create data extraction template
              </Button>
            </div>
          </Stack>
        </GrowItem>

        <div>
          <img
            src={automationPreview}
            alt="Example of suggestion UI"
            className={styles.previewImage}
          />
        </div>
      </Inline>
    </Message>
  );
};
